/**
 * Escapes special characters in a string to prevent XSS.
 * This should be used for any user-provided content that is rendered
 * via dangerouslySetInnerHTML or similar.
 */
export function escapeHtml(unsafe: string): string {
	if (!unsafe) return "";
	return unsafe
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
}

/**
 * Safely serializes data to JSON for use inside <script> tags.
 * Escapes special characters < and > to prevent XSS.
 */
export function safeJSON(data: unknown): string {
	return JSON.stringify(data).replace(/[<>]/g, c => {
		return c === "<" ? "\\u003c" : "\\u003e";
	});
}

/**
 * Redacts sensitive query parameters from a URL string to prevent sensitive data leakage in logs.
 * Supports absolute URLs and relative path+search strings.
 *
 * @param urlStr The URL string to redact (absolute or relative)
 * @returns The redacted URL string
 */
export function redactSensitiveParams(urlStr: string): string {
	if (!urlStr) return "";

	// List of case-insensitive keys to redact
	const sensitiveKeys = [
		"token",
		"password",
		"secret",
		"authorization",
		"tid",
		"access_token",
		"refresh_token",
		"api_key",
		"apikey",
		"otp",
	];

	try {
		// Check if it's an absolute URL
		const isAbsolute = urlStr.match(/^https?:\/\//i);

		if (isAbsolute) {
			const url = new URL(urlStr);
			let changed = false;
			url.searchParams.forEach((_, key) => {
				if (sensitiveKeys.includes(key.toLowerCase())) {
					url.searchParams.set(key, "[REDACTED]");
					changed = true;
				}
			});
			return changed ? url.toString() : urlStr;
		} else {
			// Handle relative URL manually to preserve path structure (e.g. leading slash, dot segments)
			// Split at the first '?' to separate path and query string
			const queryIndex = urlStr.indexOf("?");
			if (queryIndex === -1) {
				return urlStr;
			}

			const path = urlStr.substring(0, queryIndex);
			const search = urlStr.substring(queryIndex); // includes leading ?
			const searchParams = new URLSearchParams(search);
			let changed = false;

			// Iterate over keys to check against sensitive list
			searchParams.forEach((_, key) => {
				if (sensitiveKeys.includes(key.toLowerCase())) {
					searchParams.set(key, "[REDACTED]");
					changed = true;
				}
			});

			if (!changed) {
				return urlStr;
			}

			// Reconstruct URL: path + '?' + encoded params
			// URLSearchParams.toString() does not include the leading '?'
			const newSearch = searchParams.toString();
			// If we had a query string (implied by queryIndex !== -1), we should probably put back '?' even if empty?
			// But if newSearch is generated from params, it won't be empty if there are params.
			// If the original was just `?` with no keys, changed is false, so we returned early.
			return path + "?" + newSearch;
		}
	} catch {
		// Fallback: return original if parsing fails
		return urlStr;
	}
}
